import React, { useEffect, useRef, useState } from 'react';
import { X, Download, ExternalLink } from 'lucide-react';

/**
 * PDF preview modal con cleanup HARD anti-blackscreen su WebKit (Safari/Mac/iPad).
 *
 * Strategia (risolve il bug "schermo nero alla chiusura"):
 *  1. Click su X / backdrop / Esc → setUnmounting(true) → modal invisibile (visibility:hidden)
 *     ma l'iframe resta nel DOM (NON usiamo rendering condizionale che lo strappa).
 *  2. iframe.src = 'about:blank' → forza WebKit a rilasciare il renderer PDF.
 *  3. Aspettiamo l'evento 'load' di about:blank (timeout 250ms di sicurezza).
 *  4. Poi rimuoviamo l'iframe dal DOM, revochiamo l'URL blob, chiamiamo onClose.
 *  5. Doppio requestAnimationFrame per dare al compositor il tempo di flushare.
 *
 * Detection iOS rigorosa: solo iPhone/iPad reali, NON Mac M-series con touch.
 */

const isRealIOS = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPod/.test(ua)) return true;
  if (/iPad/.test(ua)) return true;
  if (
    navigator.platform === 'MacIntel' &&
    navigator.maxTouchPoints > 1 &&
    typeof window !== 'undefined' &&
    window.matchMedia &&
    !window.matchMedia('(hover: hover)').matches
  ) {
    return true;
  }
  return false;
})();

const isStandalonePWA = () => {
  try {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
           window.navigator.standalone === true;
  } catch { return false; }
};

const PdfPreviewModal = ({ pdfUrl, fileName, onClose }) => {
  const iframeRef = useRef(null);
  const closingRef = useRef(false);
  const [hidden, setHidden] = useState(false);

  // Blocca scroll del body mentre il modal è aperto
  useEffect(() => {
    if (!pdfUrl) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [pdfUrl]);

  // Cleanup di emergenza all'unmount del componente (revoke URL anche se
  // l'utente chiude la tab senza passare da handleClose).
  useEffect(() => {
    return () => {
      try {
        if (iframeRef.current) iframeRef.current.src = 'about:blank';
      } catch { /* noop */ }
      try {
        if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
      } catch { /* noop */ }
    };
  }, []);

  // Funzione di chiusura "hard cleanup"
  const handleClose = React.useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;

    // Step 1: Nascondi visivamente il modal (l'utente vede subito sparire)
    setHidden(true);

    const finalize = () => {
      try {
        if (pdfUrl && pdfUrl.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
      } catch { /* noop */ }
      // Doppio rAF per dare al compositor il tempo di flushare il renderer PDF
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (onClose) onClose();
        });
      });
    };

    const iframe = iframeRef.current;
    if (!iframe) {
      finalize();
      return;
    }

    // Step 2: punta l'iframe a about:blank → WebKit rilascia il renderer PDF
    let done = false;
    const onLoad = () => {
      if (done) return;
      done = true;
      try { iframe.removeEventListener('load', onLoad); } catch { /* noop */ }
      // Step 3: rimuovi fisicamente l'iframe dal DOM
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch { /* noop */ }
      finalize();
    };

    try { iframe.addEventListener('load', onLoad); } catch { /* noop */ }
    try { iframe.src = 'about:blank'; } catch { /* noop */ }

    // Timeout di sicurezza: se 'load' non arriva entro 250ms (es. WebKit
    // bloccato), forziamo la chiusura.
    setTimeout(() => {
      if (!done) {
        done = true;
        try { iframe.removeEventListener('load', onLoad); } catch { /* noop */ }
        try {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        } catch { /* noop */ }
        finalize();
      }
    }, 250);
  }, [pdfUrl, onClose]);

  // ESC chiude il modal
  useEffect(() => {
    if (!pdfUrl) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [pdfUrl, handleClose]);

  if (!pdfUrl) return null;

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = fileName || 'documento.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenTab = () => {
    // In PWA standalone (iOS/Android), window.open a URL blob dirotta la webview:
    // chiudendo il PDF si chiude anche l'app. Preferiamo il download sicuro.
    if (isStandalonePWA()) {
      handleDownload();
      return;
    }
    window.open(pdfUrl, '_blank');
  };

  // Backdrop click: solo se l'utente clicca DAVVERO il backdrop (non i child)
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <div
      data-testid="pdf-preview-modal"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '20px',
        // Quando hidden=true il modal scompare visivamente MA l'iframe resta
        // nel DOM finché WebKit non ha rilasciato il renderer.
        visibility: hidden ? 'hidden' : 'visible',
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? 'none' : 'auto',
        transition: 'opacity 0.15s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '900px',
          height: isRealIOS ? 'auto' : '85vh',
          display: 'flex', flexDirection: 'column',
          background: '#1a1a1a', borderRadius: '12px',
          overflow: 'hidden', border: '1px solid #333',
        }}
      >
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #333', flexShrink: 0 }}>
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '12px' }}>{fileName}</span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              data-testid="pdf-download-btn"
              onClick={handleDownload}
              style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#ffffff', color: '#000000', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Download size={16} /> Scarica
            </button>
            {!isRealIOS && !isStandalonePWA() && (
              <button
                onClick={handleOpenTab}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #555', background: 'transparent', color: '#ccc', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <ExternalLink size={16} /> Apri
              </button>
            )}
            <button
              data-testid="pdf-close-btn"
              onClick={handleClose}
              style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #555', background: '#333', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600' }}
            >
              <X size={16} /> Chiudi
            </button>
          </div>
        </div>

        {/* Content */}
        {isRealIOS ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>PDF</div>
            <p style={{ color: '#ccc', fontSize: '15px', marginBottom: '24px', lineHeight: '1.5' }}>
              Il documento <strong style={{ color: '#fff' }}>{fileName}</strong> è pronto.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleDownload}
                style={{ padding: '14px 32px', borderRadius: '10px', border: 'none', background: '#ffffff', color: '#000000', cursor: 'pointer', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={18} /> Scarica PDF
              </button>
              <button
                onClick={handleClose}
                style={{ padding: '14px 32px', borderRadius: '10px', border: '1px solid #555', background: 'transparent', color: '#ccc', cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}
              >
                Torna indietro
              </button>
            </div>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            title="Anteprima PDF"
            style={{ flex: 1, border: 'none', background: '#fff' }}
          />
        )}
      </div>
    </div>
  );
};

export default PdfPreviewModal;
